import * as z from 'zod';

export const GetBookingsSettingsRequest = z.object({});
export const GetBookingsSettingsResponse = z.object({
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the bookings settings are updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the bookings settings.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the bookings settings were created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the bookings settings were updated.')
    .optional()
    .nullable(),
  cartCheckout: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether customers can add multiple services to a cart and check out together.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Cart checkout settings, which control whether customers can add multiple services to a cart and check out together.'
    )
    .optional(),
  multiServiceAppointments: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether customers can book more than one service in a single appointment.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Multi-service appointment settings, which control whether customers can book more than one service in a single appointment.'
    )
    .optional(),
  displayTimeZone: z
    .object({
      customerCanChange: z
        .boolean()
        .describe(
          'Whether customers can switch between the business and customer time zones when viewing times.'
        )
        .optional()
        .nullable(),
      basedOn: z
        .enum(['BUSINESS_TIME_ZONE', 'CUSTOMER_TIME_ZONE'])
        .describe('Time zone that times are displayed in by default.')
        .optional(),
    })
    .describe(
      "Display time zone settings, which control whether times are shown in the business's or the customer's time zone."
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the bookings settings object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
export const UpdateBookingsSettingsRequest = z.object({
  bookingsSettings: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the bookings settings are updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the bookings settings.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the bookings settings were created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the bookings settings were updated.')
        .optional()
        .nullable(),
      cartCheckout: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether customers can add multiple services to a cart and check out together.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cart checkout settings, which control whether customers can add multiple services to a cart and check out together.'
        )
        .optional(),
      multiServiceAppointments: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether customers can book more than one service in a single appointment.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Multi-service appointment settings, which control whether customers can book more than one service in a single appointment.'
        )
        .optional(),
      displayTimeZone: z
        .object({
          customerCanChange: z
            .boolean()
            .describe(
              'Whether customers can switch between the business and customer time zones when viewing times.'
            )
            .optional()
            .nullable(),
          basedOn: z
            .enum(['BUSINESS_TIME_ZONE', 'CUSTOMER_TIME_ZONE'])
            .optional(),
        })
        .describe(
          "Display time zone settings, which control whether times are shown in the business's or the customer's time zone."
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the bookings settings object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Bookings settings to update.'),
});
export const UpdateBookingsSettingsResponse = z.object({
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the bookings settings are updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the bookings settings.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the bookings settings were created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the bookings settings were updated.')
    .optional()
    .nullable(),
  cartCheckout: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether customers can add multiple services to a cart and check out together.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Cart checkout settings, which control whether customers can add multiple services to a cart and check out together.'
    )
    .optional(),
  multiServiceAppointments: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether customers can book more than one service in a single appointment.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Multi-service appointment settings, which control whether customers can book more than one service in a single appointment.'
    )
    .optional(),
  displayTimeZone: z
    .object({
      customerCanChange: z
        .boolean()
        .describe(
          'Whether customers can switch between the business and customer time zones when viewing times.'
        )
        .optional()
        .nullable(),
      basedOn: z
        .enum(['BUSINESS_TIME_ZONE', 'CUSTOMER_TIME_ZONE'])
        .describe('Time zone that times are displayed in by default.')
        .optional(),
    })
    .describe(
      "Display time zone settings, which control whether times are shown in the business's or the customer's time zone."
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the bookings settings object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
